import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Grid, Tooltip } from '@mui/material'
import { Edit, FlagCircle } from '@mui/icons-material'
import { useGET_academicPeriodsSelectQuery } from 'redux/api/academicPeriodsApi'
import { Form, Formik } from 'formik'
import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'
import SelectField from 'components/atoms/fields/SelectField'
import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/unitModel'
import { crudColorMap, crudIconMap, gradeSelectData, translateGradeLevel, unitDurationSelectData, unitNumberSelectData, unitPeriodData, unitPeriodSelectData } from 'utils/helpers'
import { ActionType, IUnitData } from 'types'
import { useDELETE_unitMutation, useGET_unitByIdQuery, usePATCH_unitMutation, usePOST_unitMutation } from 'redux/api/unitsApi'
import { useGET_courseSelectQuery } from 'redux/api/coursesApi'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'redux/store'
import { setCurriculumSelection, setGoalbankSelection } from 'redux/ui/goalbankUI'
import RichTextField from 'components/atoms/fields/RichTextField'
import { useGET_setupOptionsQuery } from 'redux/api/setupApi'
import GoalbankSelection from 'components/utilized/goalbank/GoalbankSelection'
import DialogPopup from 'components/molecules/DialogPopup'
import GoalbankPage from 'pages/protected/GoalbankPage'
import GradeItemList from 'components/molecules/GradeItemList'
import SectionTitle from 'components/atoms/SectionTitle'
import UnitMaterials from 'components/molecules/UnitMaterials'

interface IProps {
  action: ActionType
  id: string
  initial_course_id: string
  initial_course_title: string
  initial_grade_level: string
  viewOnly: boolean
}

const UnitForm: FC<IProps> = ({ action, id, initial_grade_level, initial_course_id, initial_course_title, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const backUrl = useSelector((state: RootState) => state['curriculum'].unitBackUrl)

  const backToSlug = backUrl || `/curriculum-map/`
  const editSlug = `/units/edit/${id}`

  const { data: setupData } = useGET_setupOptionsQuery(null)

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug: backToSlug,
    entityName: `${t('goalbank.units')}`,
    entityId: id,
    getEntityQuery: useGET_unitByIdQuery,
    createEntityMutation: usePOST_unitMutation,
    editEntityMutation: usePATCH_unitMutation,
    deleteEntityMutation: useDELETE_unitMutation,
  })


  const { 
    grade_level,
    course,
    academic_period,
    number,
    title,
    duration,
    period,
    description,
    student_learning_objectives,
    essential_questions,
    enduring_understanding,
    vocabulary,
    instructional_strategies,
    activities,
    assessment,
    differentiation,
    reflection,
    extension,
    family_connection,
    sequence,
    materials
  } = formModel()


  const textFieldsMap = {
    curriculum_student_learning_objectives: student_learning_objectives,
    curriculum_essential_questions: essential_questions,
    curriculum_enduring_understanding: enduring_understanding,
    curriculum_vocabulary: vocabulary,
    curriculum_instructional_strategies: instructional_strategies,
    curriculum_activities: activities,
    curriculum_assessment: assessment,
    curriculum_differentiation: differentiation,
    curriculum_reflection: reflection,
    curriculum_extension: extension,
    curriculum_family_connection: family_connection,
    curriculum_sequence: sequence,
  }

  let qryValues = {}
  if(initial_grade_level)
    qryValues["grade_level"]={
      id: initial_grade_level,
      title: `${translateGradeLevel(initial_grade_level,true)}`
    }
  if(initial_course_id && initial_course_title)
    qryValues["course"]={
      id: initial_course_id,
      title: initial_course_title
    }

  const serverValues = entity?.id
  ? {
    ...entity,
    ...{
      grade_level: {
        id: entity.course?.grade_level,
        title: `${translateGradeLevel(entity.course?.grade_level,true)}`
      }
    }
  } : qryValues

  let formValues = {
    ...initialValues,
    ...serverValues,
  }

  formValues = {
    ...formValues,
    number: formValues.number ? {id: formValues.number, title: `${formValues.number}`} : null,
    duration: formValues.duration ? {id: formValues.duration, title: `${formValues.duration}`} : null,
    period: unitPeriodData.find(p => p.id == formValues.period) || null,
  }

  const [goalbankOpen, setGoalbankOpen] = useState(false)
  const goalbankSelection = useSelector((state: RootState) => state['goalbank'].goalbankSelection)
  const curriculumSelection = useSelector((state: RootState) => state['goalbank'].curriculumSelection)

  useEffect(() => {
    dispatch(setGoalbankSelection(entity?.goalbank_selection ?? {}))
    dispatch(setCurriculumSelection(entity?.curriculum_selection ?? {}))
  }, [entity])

  return (
    <Formik
      enableReinitialize
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IUnitData = {
          ...values,
          course_id: values.course?.id ?? null,
          academic_period_id: values.academic_period?.id ?? null,
          grade_lvel: values.number?.id ?? null,
          number:  values.number?.id ?? null,
          duration:  values.duration?.id ?? null,
          period: values.period?.id ?? null,
          goalbank_selection: goalbankSelection,
          curriculum_selection: curriculumSelection,
        }

        handleSubmit(action, id, data)
      }}
    >{({ values, setFieldValue, errors }) => (
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem', marginBottom: '2rem' }}>

            <Grid item xs={12} lg={4}>
              <SelectField
                fieldProps={{ ...grade_level }}
                apiCall={gradeSelectData}
                disabled={viewOnly}
              />
            </Grid>
             
            <Grid item xs={12} lg={4}>
              <SelectField
                fieldProps={{ ...course }}
                apiCall={useGET_courseSelectQuery}
                filters={ values?.grade_level?.id ? { grade_level: values?.grade_level?.id} : {}}
                disabled={viewOnly}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <SelectField
                fieldProps={{ ...academic_period }}
                apiCall={useGET_academicPeriodsSelectQuery}
                disabled={viewOnly}
                />
            </Grid>

            <Grid item xs={12} lg={2}>
              <SelectField
                fieldProps={{ ...number }}
                apiCall={unitNumberSelectData}
                disabled={viewOnly}
                />
            </Grid>

            <Grid item xs={12} lg={6}>
              <InputField 
                fieldProps={{ ...title }} 
                disabled={viewOnly} />
            </Grid>
            
            <Grid item xs={12} lg={2}>
              <SelectField
                fieldProps={{ ...duration }}
                apiCall={unitDurationSelectData}
                disabled={viewOnly}
                />
            </Grid>

            <Grid item xs={12} lg={2}>
              <SelectField
                fieldProps={{ ...period }}
                apiCall={unitPeriodSelectData}
                disabled={viewOnly}
                />
            </Grid>
            
            <Grid item xs={12}>
              <RichTextField
                fieldProps={{ ...description }}
                disabled={viewOnly}
                label={description.label}
              />
            </Grid>
          </Grid>

          <Grid container spacing={4}>
            <Grid item xs={12} lg={3}>
                  <Button
                    onClick={() => setGoalbankOpen(true)}
                    aria-label={`${t('goalbank.use-curriculum-library')}`}
                    size='small'
                    disabled={viewOnly}
                    color='success'
                    variant='contained'
                    sx={{ width: '300px' }}
                  >
                    <FlagCircle style={{ fontSize: '1.5rem', marginRight: '10px' }} /> {t('goalbank.use-curriculum-library')}
                  </Button>
            </Grid>
            <Grid item xs={12} lg={12}>
                <GoalbankSelection goalbankSelection={goalbankSelection} curriculumSelection={curriculumSelection} />
            </Grid>
          </Grid>

          {setupData &&
          <Grid container spacing={4} sx={{ marginTop: '2rem' }}>
              {Object.keys(textFieldsMap)
                .filter(k => setupData.filter(sd => sd.slug == k && sd.is_enabled).length == 1)
                .map((k) => 
                  <Grid key={k} item xs={6}>
                    <RichTextField
                      fieldProps={{ ...textFieldsMap[k] }}
                      disabled={viewOnly}
                      label={textFieldsMap[k].label}
                    />
                  </Grid>)
              }
          </Grid>
          }


          {/*
          <Grid container spacing={4} sx={{ marginTop: '1rem', marginBottom: '2rem' }}>
              <Grid item xs={12}>
                <SectionTitle text={`${t('common.materials')}`} />
                <UnitMaterials fieldProps={{ ...materials }} disabled={viewOnly} />
              </Grid>
          </Grid>
          */}


          <Grid container spacing={4} sx={{ marginTop: '1rem', marginBottom: '2rem' }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex' }} className="transparent-background">
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(backToSlug)}
                  sx={{ minHeight: '2.5rem', width: '10rem' }}
                >
                  <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.back')}`}
                </Button>
                {action === 'view' && 
                  <Box sx={{ marginLeft: 'auto' }}>
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(editSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('common.edit')}`}
                    </Button>
                  </Box>
                }
                {action !== 'view' && (
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    sx={{ width: '10rem' }}
                    isLoading={isLoading}
                  />
                )}
              </Box>
            </Grid>
        </Grid>


        <DialogPopup
              title={t('common.goalbank')}
              open={goalbankOpen}
              onClose={() => setGoalbankOpen(false)}
              popupWidth={'1400px'}
              >
                  <GoalbankPage 
                    hasSelection={true} 
                    filters={{
                        grade_level: parseInt(values.grade_level?.id) ?? null,
                        subject: parseInt(values.course?.subject_origin_id) ?? null,
                        domain: parseInt(values.course?.domain_id) ?? null,
                  }}/>
        </DialogPopup>
      </Form>
      )}
    </Formik>
  )
}
export default UnitForm
